import { apiClient } from './apiClient';
import { CCBIndividualProfile } from './profileTypes';

export interface CCBIndividual {
  id: number;
  first_name: string;
  last_name: string;
  name: string;
  email?: string;
  email_unsubscribed?: boolean;
  phone?: {
    mobile?: string;
    home?: string;
    work?: string;
    emergency?: string;
  };
  addresses?: {
    mailing?: {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      country_iso?: string;
    };
  };
  modified?: string;
  active?: boolean;
  images?: {
    thumbnail?: string;
    medium?: string;
    large?: string;
  };
}

export interface PeopleApiParams {
  page?: number;
  perPage?: number;
  includeInactive?: boolean;
  modifiedSince?: string;
}

export interface IndividualsResponse {
  data: CCBIndividual[];
  hasMore: boolean;
  currentPage?: number;
  lastPage?: number;
  totalLoaded: number;
}

export const peopleApi = {
  /**
   * Fetch first page of individuals (fast initial load)
   */
  async getIndividualsFirstPage(params?: PeopleApiParams): Promise<IndividualsResponse> {
    const queryParams: Record<string, string> = {
      initial: 'true',
    };
    
    if (params?.includeInactive !== undefined) {
      queryParams.include_inactive = params.includeInactive.toString();
    }
    if (params?.modifiedSince) {
      queryParams.modified_since = params.modifiedSince;
    }

    return apiClient.get<IndividualsResponse>('/individuals', {
      params: queryParams,
    });
  },

  /**
   * Fetch remaining pages in the background
   */
  async getIndividualsRemainingPages(startPage: number = 2, params?: PeopleApiParams): Promise<IndividualsResponse> {
    const queryParams: Record<string, string> = {
      startPage: startPage.toString(),
    };
    
    if (params?.includeInactive !== undefined) {
      queryParams.include_inactive = params.includeInactive.toString();
    }
    if (params?.modifiedSince) {
      queryParams.modified_since = params.modifiedSince;
    }

    return apiClient.get<IndividualsResponse>('/individuals', {
      params: queryParams,
    });
  },

  /**
   * Fetch a list of individuals from the CCB API (legacy - all at once)
   * The BFF API handles pagination automatically and returns all records
   */
  async getIndividuals(params?: PeopleApiParams): Promise<CCBIndividual[]> {
    const queryParams: Record<string, string> = {};
    
    // These params can still be used for filtering if needed
    if (params?.includeInactive !== undefined) {
      queryParams.include_inactive = params.includeInactive.toString();
    }
    if (params?.modifiedSince) {
      queryParams.modified_since = params.modifiedSince;
    }

    const individuals = await apiClient.get<CCBIndividual[]>('/individuals', {
      params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });

    return individuals;
  },

  /**
   * Get a specific individual's detailed profile by ID
   * @param individualId - The individual's ID
   * @param includeFamily - If true, includes family members in the response
   */
  async getIndividualProfile(individualId: string, includeFamily: boolean = true): Promise<CCBIndividualProfile> {
    const params = includeFamily ? { includeFamily: 'true' } : undefined;
    return apiClient.get<CCBIndividualProfile>(`/individuals/${individualId}`, { params });
  },

  /**
   * Get family members by family ID
   */
  async getFamilyMembers(familyId: string): Promise<any> {
    return apiClient.get(`/families/${familyId}`);
  },

  /**
   * Get the current user's profile
   */
  async getMe(): Promise<CCBIndividual> {
    return apiClient.get<CCBIndividual>('/me');
  },

  /**
   * Get church information
   */
  async getChurch(): Promise<any> {
    return apiClient.get('/church');
  },
};
